import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ValidationResults = ({ 
  leads = [], 
  onValidateLeads, 
  isProcessing, 
  selectedLeads = [],
  onSelectLeads
}) => {
  const [validationFilters, setValidationFilters] = useState({
    showOnlyUnvalidated: false,
    showOnlyErrors: false,
    minConfidence: 0
  });
  const [searchTerm, setSearchTerm] = useState('');

  const handleValidateSelected = () => {
    if (selectedLeads?.length > 0) {
      onValidateLeads?.(selectedLeads);
    }
  };

  const handleLeadSelect = (leadId) => {
    const isSelected = selectedLeads?.includes(leadId);
    if (isSelected) {
      onSelectLeads?.(selectedLeads?.filter(id => id !== leadId));
    } else {
      onSelectLeads?.([...selectedLeads, leadId]);
    }
  };

  const handleSelectAll = () => {
    // Get filtered leads based on current filters
    const currentFilteredLeads = leads?.filter(lead => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm?.toLowerCase();
        const matchesSearch = 
          lead?.name?.toLowerCase()?.includes(searchLower) ||
          lead?.company?.toLowerCase()?.includes(searchLower) ||
          lead?.email?.toLowerCase()?.includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Validation status filters
      if (validationFilters?.showOnlyUnvalidated && lead?.validationStatus === 'validated') {
        return false;
      }
      
      if (validationFilters?.showOnlyErrors && lead?.validationStatus !== 'needs_review') {
        return false;
      }

      // Confidence filter
      if (lead?.aiConfidence < validationFilters?.minConfidence) {
        return false;
      }

      return true;
    }) || [];
    
    const allLeadIds = currentFilteredLeads?.map(lead => lead?.id) || [];
    if (selectedLeads?.length === allLeadIds?.length) {
      onSelectLeads?.([]);
    } else {
      onSelectLeads?.(allLeadIds);
    }
  };

  const getValidationStatusIcon = (status) => {
    switch (status) {
      case 'validated': return { icon: 'CheckCircle', color: 'text-green-600' };
      case 'pending': return { icon: 'Clock', color: 'text-yellow-600' };
      case 'needs_review': return { icon: 'AlertTriangle', color: 'text-red-600' };
      default: return { icon: 'HelpCircle', color: 'text-gray-600' };
    }
  };

  const getDataQualityColor = (quality) => {
    switch (quality) {
      case 'excellent': return 'text-green-600 bg-green-50 border-green-200';
      case 'very_good': return 'text-green-600 bg-green-50 border-green-200';
      case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'fair': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'poor': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Filter leads based on validation filters and search term
  const filteredLeads = leads?.filter(lead => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      const matchesSearch = 
        lead?.name?.toLowerCase()?.includes(searchLower) ||
        lead?.company?.toLowerCase()?.includes(searchLower) ||
        lead?.email?.toLowerCase()?.includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Validation status filters
    if (validationFilters?.showOnlyUnvalidated && lead?.validationStatus === 'validated') {
      return false;
    }
    
    if (validationFilters?.showOnlyErrors && lead?.validationStatus !== 'needs_review') {
      return false;
    }

    // Confidence filter
    if (lead?.aiConfidence < validationFilters?.minConfidence) {
      return false;
    }

    return true;
  }) || [];

  const validationStats = {
    total: leads?.length || 0,
    validated: leads?.filter(lead => lead?.validationStatus === 'validated')?.length || 0,
    pending: leads?.filter(lead => lead?.validationStatus === 'pending')?.length || 0,
    needsReview: leads?.filter(lead => lead?.validationStatus === 'needs_review')?.length || 0,
    emailVerified: leads?.filter(lead => lead?.emailVerified)?.length || 0,
    phoneVerified: leads?.filter(lead => lead?.phoneVerified)?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Validation Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-foreground">{validationStats?.total}</div>
          <div className="text-sm text-muted-foreground">Total Leads</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{validationStats?.validated}</div>
          <div className="text-sm text-muted-foreground">Validated</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{validationStats?.pending}</div>
          <div className="text-sm text-muted-foreground">Pending</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{validationStats?.needsReview}</div>
          <div className="text-sm text-muted-foreground">Needs Review</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{validationStats?.emailVerified}</div>
          <div className="text-sm text-muted-foreground">Email Verified</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{validationStats?.phoneVerified}</div>
          <div className="text-sm text-muted-foreground">Phone Verified</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
          <h2 className="text-xl font-semibold text-foreground">Real-time Contact Validation</h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={handleSelectAll}
              size="sm"
            >
              {selectedLeads?.length === filteredLeads?.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              onClick={handleValidateSelected}
              disabled={selectedLeads?.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Icon name="CheckCircle" size={16} className="mr-2" />
                  Validate Selected ({selectedLeads?.length})
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            iconName="Search"
          />
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showOnlyUnvalidated"
              checked={validationFilters?.showOnlyUnvalidated}
              onChange={(checked) => setValidationFilters(prev => ({ ...prev, showOnlyUnvalidated: checked }))}
            />
            <label htmlFor="showOnlyUnvalidated" className="text-sm text-foreground cursor-pointer">
              Show unvalidated only
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showOnlyErrors"
              checked={validationFilters?.showOnlyErrors}
              onChange={(checked) => setValidationFilters(prev => ({ ...prev, showOnlyErrors: checked }))}
            />
            <label htmlFor="showOnlyErrors" className="text-sm text-foreground cursor-pointer">
              Show errors only
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-foreground">Min Confidence:</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={validationFilters?.minConfidence}
              onChange={(e) => setValidationFilters(prev => ({ ...prev, minConfidence: parseInt(e?.target?.value) || 0 }))}
              className="w-20"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {/* Validation Results Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-foreground">
                  <Checkbox
                    checked={selectedLeads?.length === filteredLeads?.length && filteredLeads?.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Contact</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Validation Status</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Email Status</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Phone Status</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Confidence</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Data Quality</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads?.map((lead) => {
                const statusInfo = getValidationStatusIcon(lead?.validationStatus);
                return (
                  <tr key={lead?.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <Checkbox
                        checked={selectedLeads?.includes(lead?.id)}
                        onChange={() => handleLeadSelect(lead?.id)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-foreground">{lead?.name}</div>
                        <div className="text-sm text-muted-foreground">{lead?.company}</div>
                        <div className="text-xs text-muted-foreground">{lead?.email}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Icon name={statusInfo?.icon} size={16} className={statusInfo?.color} />
                        <span className="text-sm capitalize">
                          {lead?.validationStatus?.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={lead?.emailVerified ? 'CheckCircle' : 'XCircle'} 
                          size={16} 
                          className={lead?.emailVerified ? 'text-green-600' : 'text-red-600'} 
                        />
                        <span className="text-sm">
                          {lead?.emailVerified ? 'Verified' : 'Invalid'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={lead?.phoneVerified ? 'CheckCircle' : 'XCircle'} 
                          size={16} 
                          className={lead?.phoneVerified ? 'text-green-600' : 'text-red-600'} 
                        />
                        <span className="text-sm">
                          {lead?.phoneVerified ? 'Verified' : 'Invalid'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div className={`text-sm font-medium ${
                          lead?.aiConfidence >= 80 ? 'text-green-600' :
                          lead?.aiConfidence >= 60 ? 'text-yellow-600': 'text-red-600'
                        }`}>
                          {lead?.aiConfidence}%
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                        getDataQualityColor(lead?.dataQuality)
                      }`}>
                        {lead?.dataQuality?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onValidateLeads?.([lead?.id])}
                        >
                          <Icon name="RefreshCw" size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => console.log('Edit lead:', lead?.id)}
                        >
                          <Icon name="Edit" size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredLeads?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No leads match the current filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationResults;